/**
 * useLocations Hook
 * ahooks data fetching hook for location data (countries, states, cities)
 */

import { useRequest } from 'ahooks';
import type { Options } from 'ahooks/lib/useRequest/src/types';
import { useTranslation } from 'react-i18next';
import { trackCacheKey } from '@/utils/cacheKeyRegistry';
import { locationApi } from '@/services/api';
import { useLocationStore } from '@/stores';
import type {
  CountryListResponse,
  StateListResponse,
  CityListResponse,
  PostcodeLocationResponse,
} from '@/types/api';

/**
 * Hook to fetch and cache countries list
 * Uses persistent storage, so data is available immediately after first load
 *
 * @param langOverride - Optional language override (e.g., "en", "zh"). If not provided, uses UI language.
 * @param config - Optional ahooks useRequest config
 */
export function useCountriesData(
  langOverride?: string | null,
  config?: Options<CountryListResponse, []>
) {
  const { i18n } = useTranslation();
  const { countries, setCountries, setLoading, setError } = useLocationStore();

  // Use langOverride if provided, otherwise fall back to UI language
  const lang = langOverride || i18n.language;

  const { data, error, loading, refresh } = useRequest(() => locationApi.getCountries(lang), {
    cacheKey: trackCacheKey(`/api/locations/countries?lang=${lang}`),
    staleTime: 30 * 60 * 1000, // 30 minutes
    refreshOnWindowFocus: false,
    refreshDeps: [lang], // Auto-refetch when language changes
    ...config,
    onSuccess: (data) => {
      setCountries(data.data);
      setLoading(false);
      setError(null);
      config?.onSuccess?.(data, []);
    },
    onError: (err) => {
      setLoading(false);
      setError(err.message || 'Failed to fetch countries');
      config?.onError?.(err, []);
    },
  });

  return {
    countries: data?.data ?? countries,
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook to fetch states for a specific country
 * Note: Does not fall back to store data because store doesn't track language.
 * Uses ahooks cache which is keyed by language for correct localized data.
 *
 * @param countryCode - ISO 3166-1 alpha-2 code (e.g., "TW", "US", "AU")
 * @param langOverride - Optional language override (e.g., "en", "zh"). If not provided, uses UI language.
 * @param config - Optional ahooks useRequest config
 */
export function useStatesData(
  countryCode: string | null,
  langOverride?: string | null,
  config?: Options<StateListResponse, []>
) {
  const { i18n } = useTranslation();
  const { setStatesForCountry, setLoading, setError } = useLocationStore();

  // Use langOverride if provided, otherwise fall back to UI language
  const lang = langOverride || i18n.language;

  const { data, error, loading, refresh } = useRequest(
    () => locationApi.getStatesByCountry(countryCode!, lang),
    {
      ready: !!countryCode,
      cacheKey: countryCode
        ? trackCacheKey(`/api/locations/countries/${countryCode}/states?lang=${lang}`)
        : undefined,
      staleTime: 30 * 60 * 1000, // 30 minutes
      refreshOnWindowFocus: false,
      refreshDeps: [countryCode, lang], // Auto-refetch when country or language changes
      ...config,
      onSuccess: (data) => {
        if (countryCode) {
          setStatesForCountry(countryCode, data.data);
          setLoading(false);
          setError(null);
        }
        config?.onSuccess?.(data, []);
      },
      onError: (err) => {
        setLoading(false);
        setError(err.message || 'Failed to fetch states');
        config?.onError?.(err, []);
      },
    }
  );

  return {
    states: data?.data ?? [],
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook to fetch cities for a specific state
 * Note: Does not fall back to store data because store doesn't track language.
 * Uses ahooks cache which is keyed by language for correct localized data.
 *
 * @param stateId - State ID
 * @param langOverride - Optional language override (e.g., "en", "zh"). If not provided, uses UI language.
 * @param config - Optional ahooks useRequest config
 */
export function useCitiesData(
  stateId: number | null,
  langOverride?: string | null,
  config?: Options<CityListResponse, []>
) {
  const { i18n } = useTranslation();
  const { setCitiesForState, setLoading, setError } = useLocationStore();

  // Use langOverride if provided, otherwise fall back to UI language
  const lang = langOverride || i18n.language;

  const { data, error, loading, refresh } = useRequest(
    () => locationApi.getCitiesByState(stateId!, lang),
    {
      ready: !!stateId,
      cacheKey: stateId
        ? trackCacheKey(`/api/locations/states/${stateId}/cities?lang=${lang}`)
        : undefined,
      staleTime: 30 * 60 * 1000, // 30 minutes
      refreshOnWindowFocus: false,
      refreshDeps: [stateId, lang], // Auto-refetch when state or language changes
      ...config,
      onSuccess: (data) => {
        if (stateId) {
          setCitiesForState(stateId, data.data);
          setLoading(false);
          setError(null);
        }
        config?.onSuccess?.(data, []);
      },
      onError: (err) => {
        setLoading(false);
        setError(err.message || 'Failed to fetch cities');
        config?.onError?.(err, []);
      },
    }
  );

  return {
    cities: data?.data ?? [],
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook to lookup location by postcode
 * Does not cache - used for one-time lookups
 */
export function usePostcodeLookup(
  country: string | null,
  postcode: string | null,
  config?: Options<PostcodeLocationResponse, []>
) {
  const { data, error, loading, refresh } = useRequest(
    () => locationApi.lookupByPostcode({ countryCode: country!, postcode: postcode! }),
    {
      ready: !!(country && postcode),
      cacheKey:
        country && postcode
          ? trackCacheKey(`/api/locations/postcode/${country}/${postcode}`)
          : undefined,
      cacheTime: 5000,
      ...config,
    }
  );

  return {
    locationData: data,
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook to preload location data for cascading selects
 * Loads countries on mount, then loads states/cities as needed
 */
export function useLocationPreload() {
  const { i18n } = useTranslation();
  const { setCountries, setStatesForCountry } = useLocationStore();

  const preloadCountries = async () => {
    try {
      const response = await locationApi.getCountries();
      setCountries(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to preload countries:', error);
      return [];
    }
  };

  const preloadStates = async (countryCode: string) => {
    try {
      const response = await locationApi.getStatesByCountry(countryCode, i18n.language);
      setStatesForCountry(countryCode, response.data);
      return response.data;
    } catch (error) {
      console.error(`Failed to preload states for ${countryCode}:`, error);
      return [];
    }
  };

  return {
    preloadCountries,
    preloadStates,
  };
}

/**
 * Composite hook for cascading location selects
 * Provides all data and loading states for country → state → city
 *
 * @param selectedCountry - ISO 3166-1 alpha-2 code (e.g., "TW", "US", "AU")
 * @param selectedState - State ID
 * @param langOverride - Optional language override (e.g., "en", "zh"). If not provided, uses UI language.
 *                       Use this to display location names in the language the address was entered in.
 */
export function useCascadingLocations(
  selectedCountry: string | null,
  selectedState: number | null,
  langOverride?: string | null
) {
  // Fetch countries (with optional language override)
  const countriesQuery = useCountriesData(langOverride);

  // Fetch states when country is selected (with optional language override)
  const statesQuery = useStatesData(selectedCountry, langOverride);

  // Fetch cities when state is selected (with optional language override)
  const citiesQuery = useCitiesData(selectedState, langOverride);

  return {
    // Data
    countries: countriesQuery.countries,
    states: statesQuery.states,
    cities: citiesQuery.cities,

    // Loading states
    isLoadingCountries: countriesQuery.isLoading,
    isLoadingStates: statesQuery.isLoading,
    isLoadingCities: citiesQuery.isLoading,

    // Errors
    countriesError: countriesQuery.error,
    statesError: statesQuery.error,
    citiesError: citiesQuery.error,

    // Refresh methods
    refreshCountries: countriesQuery.refresh,
    refreshStates: statesQuery.refresh,
    refreshCities: citiesQuery.refresh,
  };
}
